<template>
  <v-container>
    <div v-if="username" id="homeView">
      <v-row justify="center">
        <v-col id="filter" cols="12" md="2" order="1" class="pa-0 mx-sm-4 mb-4" style="max-width: 600px">
          <v-card v-if="reviews.length > 0 || filterRadio !== 'none'">
            <h3 class="text-center">Filter</h3>
            <v-radio-group
              v-model="filterRadio"
              class="ml-3"
              :class="{ 'mt-1': this.$vuetify.breakpoint.xs }"
              @change="filter"
            >
              <div class="none-radio">
                <v-radio label="none" value="none" />
              </div>
              <v-row no-gutters>
                <v-col v-for="medium in $mediums" :key="medium + 'Filter'" class="pa-0" cols="6" md="12">
                  <v-radio :label="userFriendlyMediumWord(medium)" :value="medium" />
                </v-col>
              </v-row>
            </v-radio-group>
          </v-card>
        </v-col>

        <v-col id="main-feed" cols="12" sm="9" order="3" order-sm="2" style="max-width: 600px" class="pa-0 mt-md-0">
          <v-card class="pa-2 text-center mb-5">
            <h3 class="text-center">Create a New Rating!</h3>
            <v-btn large color="accent" dark :to="{ name: 'rate' }"> Rate </v-btn>
          </v-card>

          <no-feed v-if="endOfReviews && reviews.length === 0 && filterRadio === 'none'"></no-feed>

          <div v-for="(review, index) in reviews" :key="review.id" class="post mb-6">
            <home-feed-review :review="review" @review-updated="handleReviewUpdated($event, index)" />
          </div>

          <div class="text-center">
            <v-btn v-if="!endOfReviews" :loading="loading" @click="getReviews"> More Posts </v-btn>
          </div>
        </v-col>

        <v-col v-if="!$vuetify.breakpoint.smAndDown" class="pt-0 pr-0" sm="2" order="2" order-sm="3">
          <v-card class="pa-5 text-center" :height="$vuetify.breakpoint.xs ? '100%' : undefined">
            <router-link :to="{ name: 'feedback' }"> Provide Feedback </router-link>
          </v-card>

          <v-card v-if="friendSuggestions.length > 0" class="mt-3 pb-4">
            <v-card-title>Suggestions</v-card-title>

            <v-card-text v-for="user in friendSuggestions" v-bind:key="'suggestion' + user" class="py-0">
              <router-link :to="{ name: 'profile', params: { username: user } }">{{ user }}</router-link>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <home-no-login v-else />

    <v-dialog v-model="deleteComment.dialog">
      <v-card>
        <v-card-title>Are you sure you want to delete?</v-card-title>
        <v-card-actions>
          <v-btn @click="deleteComment.dialog = false" color="primary"> No </v-btn>
          <v-spacer></v-spacer>
          <v-btn @click="confirmDeleteComment" color="red"> Yes </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import homeNoLogin from "./home-no-login.vue";
import noFeed from "./no-feed.vue";
import HomeFeedReview from "../../components/review-tile-home-feed.vue";
import { userFriendlyMediumWord } from "@/util/mediumHelpers";

import { mapMutations } from "vuex";

export default {
  name: "HomePage",
  components: { homeNoLogin, noFeed, HomeFeedReview },

  data: function () {
    return {
      expanded: {},
      deleteDialog: false,
      search: "",
      dialogVisible: false,

      reportDialog: false,
      reportId: 0,

      deleteComment: {
        dialog: false,
        id: undefined,
        review_id: undefined,
      },

      reviews: [],
      reviewSet: 0,
      loading: true,
      endOfReviews: false,
      filterRadio: "none",

      name: "",
      nameRules: [(v) => !!v || "Name is required"],
      select: null,
      comments: {},
      profilePics: {},

      friendSuggestions: [],
    };
  },

  computed: {
    username() {
      return this.$store.state.username;
    },
  },

  watch: {
    // Populate Home Feed on login
    username(newUsername) {
      this.clearReviews();
      if (newUsername) {
        this.getReviews();
      }
    },
  },

  mounted: async function () {
    this.$root.$on("delete-review", this.handleDeleteReview);
    this.$root.$on("delete-comment", this.handleDeleteComment);

    if (this.$store.state.username) {
      if (this.$store.state.homePage.results.length === 0) {
        await this.getReviews();
      } else {
        // Check for new reviews
        const response = await this.$axios.get(`/v1/reviews`, {
          params: {
            friendship: "friendAndMe",
            mediums: this.filterRadio === "none" ? undefined : [this.filterRadio],
            limit: 10,
            offset: this.reviewSet,
          },
        });

        // No new reviews
        if (response.data.items[0].id === this.$store.state.homePage.results[0].id) {
          this.reviews = this.$store.state.homePage.results;
          this.reviewSet = this.$store.state.homePage.reviewSetIdx;
          this.endOfReviews = this.$store.state.homePage.endOfReviews;
          this.loading = false;
          await this.$vuetify.goTo(this.$store.state.homePage.scrollY, {
            duration: 0,
          });
        }
        // New Reviews
        else {
          this.reviews = response.data.items;
          this.reviewSet = 10;
          this.loading = false;
        }
      }

      this.$axios.get("/friend-suggestions").then((response) => {
        this.friendSuggestions = response.data;
      });
    }

    window.addEventListener("scroll", this.handleScroll);
  },

  beforeRouteLeave(to, from, next) {
    // Only save homepage if logged in
    if (this.username) {
      this.saveHomePage({
        results: this.reviews,
        scrollY: document.documentElement.scrollTop,
        reviewSetIdx: this.reviewSet,
        endOfReviews: this.endOfReviews,
      });
    }

    next();
  },

  destroyed: function () {
    window.removeEventListener("scroll", this.handleScroll);
  },

  methods: {
    ...mapMutations(["showSnackbar", "saveHomePage"]),
    userFriendlyMediumWord,

    handleScroll: async function () {
      if (this.loading) return;
      let bottom = document.getElementById("main-feed").getBoundingClientRect().bottom;

      if (bottom - 500 < window.innerHeight && !this.endOfReviews) {
        let newEntries = await this.getReviews();
        if (newEntries.length < 10) this.endOfReviews = true;
      }
    },

    clearReviews: function () {
      this.reviews = [];
      this.endOfReviews = false;
      this.reviewSet = 0;
    },

    getReviews: async function () {
      console.log(this.filterRadio);
      this.loading = true;

      const response = await this.$axios.get(`/v1/reviews`, {
        params: {
          friendship: "friendAndMe",
          mediums: this.filterRadio === "none" ? undefined : [this.filterRadio],
          limit: 10,
          offset: this.reviewSet,
        },
      });

      if (response.data.items.length > 0) {
        this.reviews = this.reviews.concat(response.data.items);
      } else {
        this.endOfReviews = true;
      }
      this.reviewSet += 10;
      this.loading = false;

      return response.data.items;
    },

    filter: function () {
      this.clearReviews();
      this.getReviews();
    },

    confirmDeleteComment: function () {
      this.$axios
        .delete("/comment", {
          params: {
            id: this.deleteComment.id,
          },
        })
        .then(() => {
          this.deleteComment.dialog = false;
          for (let review of this.reviews) {
            if (review.id === this.deleteComment.review_id) {
              review.comments = review.comments.filter((comment) => comment.id !== this.deleteComment.id);
            }
          }
        })
        .finally(() => {
          this.deleteComment.id = undefined;
          this.deleteComment.review_id = undefined;
        });
    },

    // event is a review object
    handleReviewUpdated(event, index) {
      this.reviews[index] = event;
    },

    handleDeleteReview(id) {
      let indexOfReview = this.reviews.findIndex((review) => review.id === id);
      if (indexOfReview >= 0) {
        this.$delete(this.reviews, indexOfReview);
      }
    },

    handleDeleteComment(comment) {
      const review = this.reviews.find((review) => review.id === comment.reviewId);
      if (!review) return;
      const indexOfComment = review.comments.items.findIndex((c) => c.id.toString() === comment.id);
      if (indexOfComment < 0) return;
      this.$delete(review.comments.items, indexOfComment);
    },
  },
};
</script>

<style>
html,
body {
  font-family: "Roboto", sans-serif;
}

a:link {
  text-decoration: none;
}

.none-radio {
  width: 100%;
  margin-bottom: 16px;
}
</style>
